import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Isvg from 'react-inlinesvg';

import Badge from 'components/Badge';
import Button from 'components/Button';
import Callout from 'components/Callout';
import { Card, CardSection, CardTitle } from 'components/Card';
import Message from 'components/Message';
import Spinner from 'components/Spinner';
import { PlainSwitch } from 'components/Switch';
import { ICONS } from 'constants/icons';

import useModifierGroups from '../useModifierGroups';
import useModifierMutations from '../useModifierMutations';
import GroupStatusBadge from './GroupStatusBadge';
import messages from './ModifiersTabPanel.messages';
import './ModifiersTabPanel.scss';

// The sandbox form only creates single-select groups; the real editor (SKU-687+) will
// expose min/max controls.
const SINGLE_SELECT_MAX = 1;

const parseOptions = raw =>
  raw
    .split(',')
    .map(name => name.trim())
    .filter(Boolean)
    .map((name, index) => ({ name, ordinal: index + 1, isDefault: index === 0, enabled: true, soldOut: false }));

const ModifiersTabPanel = ({ venueId }) => {
  const intl = useIntl();
  const groupsQuery = useModifierGroups(venueId);
  const { createGroup, setModifierSoldOut } = useModifierMutations({ venueId });

  const [name, setName] = useState('');
  const [optionsRaw, setOptionsRaw] = useState('');
  const [required, setRequired] = useState(true);

  const handleCreate = event => {
    event.preventDefault();
    if (!name.trim()) return;
    createGroup.mutate({
      name: name.trim(),
      selectionsMin: required ? 1 : 0,
      selectionsMax: SINGLE_SELECT_MAX,
      allowsQuantities: false,
      modifiers: parseOptions(optionsRaw),
    });
    setName('');
    setOptionsRaw('');
  };

  const groups = groupsQuery.data ?? [];
  const isEmpty = !groupsQuery.isLoading && !groupsQuery.isError && groups.length === 0;

  return (
    <div className="modifiers-dev">
      <Callout icon={<Isvg src={ICONS.info} />}>
        <Message as="text" message={messages.devNotice} />
      </Callout>

      <div className="modifiers-dev__layout">
        <aside className="modifiers-dev__sidebar">
          <Card>
            <CardTitle>
              <Message as="text" message={messages.newGroupHeading} />
            </CardTitle>
            <CardSection>
              <form className="modifiers-dev__form" onSubmit={handleCreate}>
                <label className="modifiers-dev__field">
                  <Message as="span" className="modifiers-dev__field-label" message={messages.groupNameLabel} />
                  <input
                    className="modifiers-dev__input"
                    aria-label={intl.formatMessage(messages.groupNameLabel)}
                    value={name}
                    placeholder={intl.formatMessage(messages.groupNamePlaceholder)}
                    onChange={event => setName(event.target.value)}
                  />
                </label>
                <label className="modifiers-dev__field">
                  <Message as="span" className="modifiers-dev__field-label" message={messages.optionsLabel} />
                  <input
                    className="modifiers-dev__input"
                    aria-label={intl.formatMessage(messages.optionsLabel)}
                    value={optionsRaw}
                    placeholder={intl.formatMessage(messages.optionsPlaceholder)}
                    onChange={event => setOptionsRaw(event.target.value)}
                  />
                  <Message as="span" className="modifiers-dev__hint" message={messages.optionsHint} />
                </label>
                <label className="modifiers-dev__checkbox">
                  <input type="checkbox" checked={required} onChange={event => setRequired(event.target.checked)} />
                  <Message as="span" message={messages.requiredLabel} />
                </label>
                <Button
                  className="modifiers-dev__submit"
                  type="submit"
                  styled="primary"
                  waiting={createGroup.isLoading}
                  disabled={!name.trim()}>
                  <Message as="text" message={messages.createButton} />
                </Button>
              </form>
            </CardSection>
          </Card>
        </aside>

        <section className="modifiers-dev__list">
          <div className="modifiers-dev__list-header">
            <CardTitle>
              <Message as="text" message={messages.listHeading} />
            </CardTitle>
            {!groupsQuery.isLoading && <span className="modifiers-dev__count">{groups.length}</span>}
          </div>

          {groupsQuery.isLoading && <Spinner />}
          {groupsQuery.isError && <Message message={messages.loadError} />}
          {isEmpty && (
            <Card className="modifiers-dev__empty">
              <Message message={messages.emptyState} />
            </Card>
          )}

          {groups.map(group => (
            <Card key={group.core.id} className="modifiers-dev__group">
              <div className="modifiers-dev__group-header">
                <div className="modifiers-dev__group-title">
                  <span className="modifiers-dev__group-name">{group.core.name}</span>
                  <GroupStatusBadge group={group} />
                </div>
                {group.core.selectionsMin > 0 ? (
                  <Message
                    as="span"
                    className="modifiers-dev__meta"
                    message={messages.rulesRequired}
                    values={{ min: group.core.selectionsMin, max: group.core.selectionsMax }}
                  />
                ) : (
                  <Message
                    as="span"
                    className="modifiers-dev__meta"
                    message={messages.rulesOptional}
                    values={{ max: group.core.selectionsMax }}
                  />
                )}
              </div>

              <ul className="modifiers-dev__options">
                {group.modifiers.map(modifier => (
                  <li
                    key={modifier.core.id}
                    className={`modifiers-dev__option${modifier.soldOut ? ' modifiers-dev__option--sold-out' : ''}`}>
                    <span className="modifiers-dev__option-name">
                      {modifier.core.name}
                      {modifier.core.isDefault && (
                        <Badge light className="modifiers-dev__default-badge">
                          <Message as="text" message={messages.defaultBadge} />
                        </Badge>
                      )}
                    </span>
                    <div className="modifiers-dev__availability">
                      <Badge warn={modifier.soldOut} info={!modifier.soldOut}>
                        <Message as="text" message={modifier.soldOut ? messages.soldOutOn : messages.soldOutOff} />
                      </Badge>
                      <PlainSwitch
                        name={`soldout-${modifier.core.id}`}
                        checked={modifier.soldOut}
                        inputProps={{
                          'aria-label': intl.formatMessage(messages.soldOutAria, { name: modifier.core.name }),
                          onChange: event =>
                            setModifierSoldOut.mutate({ modifierId: modifier.core.id, soldOut: event.target.checked }),
                        }}
                      />
                    </div>
                  </li>
                ))}
                {group.modifiers.length === 0 && (
                  <li className="modifiers-dev__option modifiers-dev__option--empty">
                    <Message as="text" message={messages.noOptions} />
                  </li>
                )}
              </ul>
            </Card>
          ))}
        </section>
      </div>
    </div>
  );
};

ModifiersTabPanel.propTypes = {
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ModifiersTabPanel;
